/**
 * A lazy screen whose JS chunk could not be downloaded.
 *
 * Every app-web deploy renames the chunks (content hashes) and the old files are gone from Amplify, so a tab
 * opened before the deploy 404s on the first screen it has not visited yet. webpack rejects the `lazy()`
 * import with a `ChunkLoadError`; the message check covers the CSS variant and browsers that drop the name.
 */
export const isChunkLoadError = (error: unknown): boolean => {
  const e = error as any;
  if (!e) return false;
  if (e.name === "ChunkLoadError") return true;
  const message = typeof e.message === "string" ? e.message : "";
  return (
    /Loading (CSS )?chunk [\w-]+ failed/i.test(message) ||
    /Failed to fetch dynamically imported module/i.test(message)
  );
};

/** sessionStorage key holding the time of the last reload a chunk failure triggered. */
export const CHUNK_RELOAD_AT_STORAGE_KEY = "chunk-reload-at";

/** A second chunk failure inside this window means the reload did not help — stop and show the error. */
export const CHUNK_RELOAD_WINDOW_MS = 10000;

/**
 * Reload once per window, never in a loop. After a deploy a reload fetches the new `index.html` and its new
 * chunk names, which fixes it; if the chunk is missing for some other reason (Amplify itself down, an
 * offline device) reloading again would only spin, so the second failure falls through to the error screen.
 */
export const shouldReloadForChunkError = (
  now: number,
  lastReloadAt: string | null
): boolean => {
  const last = Number(lastReloadAt);
  if (!lastReloadAt || !Number.isFinite(last)) return true;
  return now - last > CHUNK_RELOAD_WINDOW_MS;
};
